'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import type { OlcFormResolved } from './types';
import { Button } from '@/components/Button';
import { useAppForm } from '@/lib/form/hook';
import { createFormSchemas } from '@/lib/form/schemas';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useSubstitute } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';

const OLC_FORM_DEFAULTS = {
  firstname: '',
  lastname: '',
  email: '',
  consent: false,
};

/**
 * Builds the OLC target from `targetUrl` plus the three submitted values.
 *
 * `URL`/`searchParams` rather than string concatenation so a `targetUrl` that
 * already carries a query string keeps it and the values are encoded.
 * Exported for the unit tests.
 *
 * The CMS enforces an absolute `http(s)` URL, so this is a belt-and-braces
 * guard for content authored before that rule: `null` rather than a throw out
 * of the submit handler. Deliberately not resolved against the current origin
 * — the OLC target is the one shared absolute `.de` URL, never a local path.
 */
export function buildOlcRedirectUrl(
  targetUrl: string,
  values: { firstname: string; lastname: string; email: string },
): string | null {
  let url: URL;
  try {
    url = new URL(targetUrl);
  } catch {
    return null;
  }
  url.searchParams.set('firstname', values.firstname.trim());
  url.searchParams.set('lastname', values.lastname.trim());
  url.searchParams.set('email', values.email.trim());
  return url.toString();
}

/**
 * The Online-LernCenter trial form (SH-375), rendered in the Hero's content
 * slot in place of the Location Card.
 *
 * Deliberately *not* an `adjustable-contact-form`: nothing is posted to our
 * backend. A valid submit fires `submitTracking` and then hands the three
 * values to the OLC as query parameters via a full page navigation — from
 * there on the visitor is in the OLC frontend. There is consequently no
 * in-flight state, no error banner and no success card, and no success-only
 * `tracking` field: the redirect *is* the outcome.
 */
export function OlcFormComponent({
  resolved,
  className = '',
}: {
  resolved: OlcFormResolved;
  className?: string;
}) {
  const t = useTranslations('olcForm');
  const tForm = useTranslations('form');
  const tValidation = useTranslations('validation');
  const sub = useSubstitute();
  const schemas = createFormSchemas(tValidation);

  const title = sub(resolved.title ?? '') || t('title');
  const submitLabel = sub(resolved.submitLabel ?? '') || t('submitLabel');
  const loginLabel = sub(resolved.loginLabel ?? '') || t('loginLabel');
  const requiredHint = sub(resolved.requiredHint ?? '') || t('requiredHint');
  const loginUrl = substituteHref(resolved.loginUrl, sub);

  const form = useAppForm({
    defaultValues: OLC_FORM_DEFAULTS,
    onSubmit: ({ value }) => {
      const href = buildOlcRedirectUrl(resolved.targetUrl, value);
      if (!href) {
        return;
      }
      pushCmsEventTracking(resolved.submitTracking, { label: submitLabel });
      // A cross-origin app, so neither `next/link` nor `router.push` applies —
      // and not `toLiveHref` either, which only rewrites internal hrefs.
      window.location.assign(href);
    },
  });

  return (
    <div
      // The reference design sizes the pills at 50px, not the 55px the page
      // shell sets on `<main>`. `FormInput` and `Button` both read
      // `--input-height`, so one inherited override sizes the whole card.
      style={{ '--input-height': '50px' } as React.CSSProperties}
      className={classNames(
        // Both stops come straight from the live original
        // (`.tab-content-gradient` on schuelerhilfe.de/onlinelerncenter):
        //
        //   desktop            linear-gradient(#fff    0, #faf5eb 100%)
        //   max-width: 991px   linear-gradient(#faf5eb 0, #fff    100%)
        //
        // Same two colours at both sizes — only the direction flips, so one
        // `from`/`to` pair plus a direction variant reproduces it. `#faf5eb`
        // is `--color-secondary-lighter`, which keeps it per-brand.
        //
        // The flip is at `lg`, not `md`: the original's 991px sits nearest
        // Tailwind's 1024px, and `lg` is also where the Hero switches to its
        // row layout, so the gradient turns over exactly when the card moves
        // from below the title to beside it.
        //
        // Spelled with Tailwind's gradient utilities rather than the
        // `.section-fade` / `.section-fade-bottom` helpers: those are plain
        // unlayered CSS rules, so they take no breakpoint variant
        // (`md:section-fade` compiles to nothing) and, being unlayered, would
        // also outrank any utility override.
        //
        // It must be the v4 `bg-linear-to-*` name, not the legacy
        // `bg-gradient-to-*` alias. `classNames` is `twMerge(clsx(...))`, and
        // tailwind-merge 3.x does not know the old name: it buckets it with
        // `bg-color` instead of `bg-image`, sees a conflict with the
        // `bg-white` below, and silently drops it — leaving the card flat
        // white. (The hero's own `bg-gradient-to-b` overlays get away with it
        // only because they are literal `className` strings that never pass
        // through twMerge.)
        'from-secondary-lighter bg-linear-to-b to-white lg:bg-linear-to-t',
        'text-foreground w-full rounded-t-2xl bg-white p-6 pb-0 md:pb-6',
        className,
      )}
    >
      {/* Medium, not bold, and untracked: the reference title measures a 2px
          stem at a 20px size, where bold measures 3px. */}
      <h2 className="mt-2 mb-4 text-center text-base leading-tight font-medium md:mt-4 md:mb-6 md:text-lg lg:text-xl">
        {title}
      </h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        noValidate
      >
        {/* The pills' own text is 16px in the reference, which `FormInput`
            hardcodes at `text-sm`, so it is lifted here rather than in the
            shared field. */}
        <div className="flex flex-col gap-4 md:gap-5 [&_input]:text-base">
          <form.AppField
            name="firstname"
            validators={{
              onBlur: schemas.firstnameSchema,
              onSubmit: schemas.firstnameSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={tForm('firstname')}
                autoComplete="given-name"
                required
                placeholder={
                  sub(resolved.firstNamePlaceholder ?? '') || undefined
                }
              />
            )}
          </form.AppField>

          <form.AppField
            name="lastname"
            validators={{
              onBlur: schemas.lastnameSchema,
              onSubmit: schemas.lastnameSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={tForm('lastname')}
                autoComplete="family-name"
                required
                placeholder={
                  sub(resolved.lastNamePlaceholder ?? '') || undefined
                }
              />
            )}
          </form.AppField>

          <form.AppField
            name="email"
            validators={{
              onBlur: schemas.emailSchema,
              onSubmit: schemas.emailSchema,
            }}
          >
            {(field) => (
              <field.TextField
                label={tForm('email')}
                type="email"
                autoComplete="email"
                required
                placeholder={sub(resolved.emailPlaceholder ?? '') || undefined}
              />
            )}
          </form.AppField>

          <form.AppField
            name="consent"
            validators={{
              onChange: schemas.consentSchema,
              onSubmit: schemas.consentSchema,
            }}
          >
            {(field) => (
              <field.Checkbox>
                {/* The whole label, AGB and Datenschutz links included, is one
                    editor-authored blocks field (SH-375 RQ-7), so no URL is
                    hardcoded here. Paragraph margins are dropped so a
                    single-paragraph text sits flush with the box. Links carry
                    the primary colour and underline on hover only, matching
                    the Flexible Content Card's rich-text links. */}
                <div className="[&_a]:text-primary [&_a:hover]:underline [&_p]:mb-0">
                  <RichText nodes={resolved.consentText} linksInNewTab />
                </div>
              </field.Checkbox>
            )}
          </form.AppField>

          {requiredHint && (
            <p className="text-secondary m-0 text-sm md:text-base">
              {requiredHint}
            </p>
          )}

          {/* No `disabled`: the button stays clickable while fields are empty
              and the click runs validation (10.03.04.01 AC 5). There is no
              in-flight state to disable for — there is no request. */}
          {/* `text-lg!` beats `Button`'s own `text-sm/5`: the reference sets
              both button labels at 18px. */}
          <Button
            type="submit"
            variant="primary"
            className="w-full md:text-lg!"
          >
            {submitLabel}
          </Button>

          {loginUrl && (
            <Button
              href={loginUrl}
              variant="secondary"
              // `mt-6` on top of the column's `gap-4` gives the 40px the
              // reference leaves between the two buttons.
              className="mt-6 mb-10 w-full md:text-lg!"
              onClick={() =>
                pushCmsEventTracking(resolved.loginTracking, {
                  label: loginLabel,
                })
              }
            >
              {loginLabel}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
